import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";
import Logo3D from "../components/Logo3D";
import { CategoryIcon } from "../components/CategoryIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_vandys-gallery/artifacts/accmjott_vandy%27s%20logo.PNG";

// Graph paper grid pattern — light blue lines on cream
const GRID_SVG = `url("data:image/svg+xml,%3Csvg width='50' height='50' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='50' y1='0' x2='50' y2='50' stroke='%2387CEEB' stroke-width='1.5'/%3E%3Cline x1='0' y1='50' x2='50' y2='50' stroke='%2387CEEB' stroke-width='1.5'/%3E%3C/svg%3E")`;

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const { addItem } = useCart();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    axios
      .get(`${API}/products?featured=true`)
      .then((res) => setFeatured(res.data))
      .catch(console.error);
  }, []);

  return (
    <div data-testid="home-page">

      {/* ─── Hero ─── */}
      <section
        data-testid="hero-section"
        className="min-h-[80vh] flex items-center justify-center pt-16 relative overflow-hidden"
        style={{ backgroundColor: "transparent" }}
      >
        {/* Subtle grid accent on hero */}
        <div className="absolute inset-0 z-0 pointer-events-none" />

        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 w-full relative z-10">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
            {/* Left — Text */}
            <div className="order-2 md:order-1 text-center md:text-left">
              <p
                className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[#E01A23] mb-3 md:mb-4 opacity-0 animate-fade-in-up"
                style={{ fontFamily: "'Courier New', Courier, monospace" }}
              >
                handcrafted illustrations
              </p>
              <h1
                className="text-3xl sm:text-5xl lg:text-7xl tracking-tighter font-light text-[#1A1A1A] leading-[0.95] opacity-0 animate-fade-in-up stagger-1"
                style={{ fontFamily: "'Lazy Dog', cursive" }}
              >
                art you can
                <br />
                <span className="italic text-[#E01A23]">live with</span>
              </h1>
              <p
                className="mt-4 md:mt-6 text-sm md:text-lg text-[#3a3a3a] max-w-lg mx-auto md:mx-0 leading-relaxed opacity-0 animate-fade-in-up stagger-2"
                style={{ fontFamily: "'Courier New', Courier, monospace" }}
              >
                illustrated prints, stickers, and handmade goods — made with love and a whole lot of color
              </p>
              <div className="mt-6 md:mt-10 flex items-center gap-3 md:gap-4 justify-center md:justify-start opacity-0 animate-fade-in-up stagger-3 flex-wrap">
                <Link to="/shop">
                  <Button
                    data-testid="hero-shop-btn"
                    className="bg-[#E01A23] hover:bg-[#c4161e] text-white h-10 md:h-12 px-6 md:px-10 text-xs md:text-sm tracking-wider rounded-full"
                    style={{ fontFamily: "'Courier New', Courier, monospace" }}
                  >
                    shop now
                    <ArrowRight size={16} weight="bold" className="ml-2" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button
                    data-testid="hero-about-btn"
                    variant="outline"
                    className="border-[#1A1A1A]/20 text-[#1A1A1A] hover:border-[#E01A23] hover:text-[#E01A23] h-10 md:h-12 px-5 md:px-8 text-xs md:text-sm tracking-wider rounded-full"
                    style={{ fontFamily: "'Courier New', Courier, monospace" }}
                  >
                    our story
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right — 3D Logo */}
            <div className="order-1 md:order-2 h-[280px] sm:h-[350px] md:h-[550px] relative">
              <Logo3D />
              {/* Glow behind logo */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[400px] md:h-[400px] bg-[#E01A23]/5 rounded-full blur-[80px] pointer-events-none z-0" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Featured Products ─── */}
      <section className="py-12 md:py-16 relative z-10" data-testid="featured-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-8">
            <h2
              className="text-3xl md:text-4xl tracking-tight font-normal text-[#1A1A1A]"
              style={{ fontFamily: "'Lazy Dog', cursive" }}
            >
              featured
            </h2>
            <Link
              to="/shop"
              data-testid="view-all-link"
              className="flex items-center gap-2 text-sm text-[#3a3a3a] hover:text-[#E01A23] transition-colors"
              style={{ fontFamily: "'Courier New', Courier, monospace" }}
            >
              view all
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
            {featured.slice(0, 4).map((product, index) => (
              <div
                key={product.id}
                data-testid={`featured-product-${product.id}`}
                className={`group opacity-0 animate-fade-in-up stagger-${index + 1}`}
              >
                <Link to={`/product/${product.id}`}>
                  <div className="overflow-hidden rounded-xl aspect-square bg-[#EBD988]">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </Link>
                <div className="mt-3">
                  <Link to={`/product/${product.id}`}>
                    <h3
                      className="text-sm text-[#1A1A1A] hover:text-[#E01A23] transition-colors"
                      style={{ fontFamily: "'Courier New', Courier, monospace" }}
                    >
                      {product.name}
                    </h3>
                  </Link>
                  <div className="flex items-center justify-between mt-1">
                    <span
                      className="text-sm text-[#3a3a3a]"
                      style={{ fontFamily: "'Courier New', Courier, monospace" }}
                    >
                      {formatPrice(product.price)}
                    </span>
                    <button
                      data-testid={`add-to-cart-featured-${product.id}`}
                      onClick={() => addItem(product)}
                      className="text-xs text-[#E01A23] hover:underline transition-colors"
                      style={{ fontFamily: "'Courier New', Courier, monospace" }}
                    >
                      + add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Categories ─── */}
      <section className="py-12 md:py-16 bg-[#EBD988] relative z-10" data-testid="categories-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <h2
            className="text-3xl md:text-4xl tracking-tight font-normal text-[#1A1A1A] mb-8"
            style={{ fontFamily: "'Lazy Dog', cursive" }}
          >
            browse by category
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {["Art Prints", "Stickers", "Stationery", "Accessories", "Home"].map(
              (cat) => (
                <Link
                  key={cat}
                  to={`/shop?category=${cat}`}
                  data-testid={`category-link-${cat.toLowerCase().replace(/\s/g, '-')}`}
                  className="group bg-[#F5E6A0] hover:bg-white rounded-xl p-6 md:p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-md flex flex-col items-center gap-3"
                >
                  <CategoryIcon category={cat} size={32} className="text-[#E01A23] opacity-70 group-hover:opacity-100 transition-opacity" />
                  <h3
                    className="text-base md:text-lg text-[#1A1A1A] group-hover:text-[#E01A23] transition-colors"
                    style={{ fontFamily: "'Lazy Dog', cursive" }}
                  >
                    {cat.toLowerCase()}
                  </h3>
                </Link>
              )
            )}
          </div>
        </div>
      </section>

      {/* ─── About Banner ─── */}
      <section className="py-20 md:py-28 relative z-10" data-testid="about-banner-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="bg-[#E01A23] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
            {/* Grid overlay on banner */}
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.12]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='50' height='50' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='50' y1='0' x2='50' y2='50' stroke='white' stroke-width='1'/%3E%3Cline x1='0' y1='50' x2='50' y2='50' stroke='white' stroke-width='1'/%3E%3C/svg%3E")`,
                backgroundSize: "50px 50px",
              }}
            />
            <div className="relative z-10">
              <h2
                className="text-3xl md:text-4xl tracking-tight font-normal text-white mb-4"
                style={{ fontFamily: "'Lazy Dog', cursive" }}
              >
                made by hand, <span className="italic">shared with heart</span>
              </h2>
              <p
                className="text-base text-white/80 max-w-lg mx-auto mb-6"
                style={{ fontFamily: "'Courier New', Courier, monospace" }}
              >
                every piece from vandy's is drawn, painted, or crafted by hand. we believe in slow art and big personality.
              </p>
              <Link to="/about">
                <Button
                  data-testid="about-cta-btn"
                  className="bg-white text-[#E01A23] hover:bg-[#F5E6A0] h-11 px-8 rounded-full text-sm tracking-wider"
                  style={{ fontFamily: "'Courier New', Courier, monospace" }}
                >
                  learn more
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
